// Presentational cell primitives for the WickScope Trades grid + session view.
//
// Small, data-free building blocks shared by the flat ("Refined") grid, the
// grouped session table, and the dashboard-adjacent surfaces: a deterministic
// 2-letter symbol avatar, a LONG/SHORT badge, a diverging P&L magnitude gauge,
// tag pills, and the scalper run-count pill. Kept free of API types so they
// compose against either a trade row or a group header.

import type { ReactNode } from "react";
import { StickyNote } from "lucide-react";

// Deterministic avatar tints (soft WickScope-adjacent paper tones). The symbol
// hashes to a stable slot so the same ticker always renders the same color.
const AVATAR_TINTS: ReadonlyArray<{ bg: string; fg: string }> = [
  { bg: "#e7efe9", fg: "#2f8a5d" },
  { bg: "#f3e7dd", fg: "#a94e1c" },
  { bg: "#e6e9f1", fg: "#3a5a8a" },
  { bg: "#f1e7ee", fg: "#8a3a6a" },
  { bg: "#eef0e2", fg: "#6a7a25" },
  { bg: "#f3ece0", fg: "#9a7a1f" },
];

const FALLBACK_TINT = { bg: "#ece7df", fg: "#3a332b" };

function tintForSymbol(symbol: string): { bg: string; fg: string } {
  let hash = 0;
  for (let i = 0; i < symbol.length; i += 1) {
    hash = (hash * 31 + symbol.charCodeAt(i)) >>> 0;
  }
  return AVATAR_TINTS[hash % AVATAR_TINTS.length] ?? FALLBACK_TINT;
}

export function SymbolAvatar({
  symbol,
  size = "md",
}: {
  symbol: string | null | undefined;
  size?: "sm" | "md";
}): ReactNode {
  const ticker = (symbol ?? "—").trim() || "—";
  const label = ticker === "—" ? "—" : ticker.slice(0, 2).toUpperCase();
  const tint = tintForSymbol(ticker);
  const dim = size === "sm" ? "h-6 w-6 text-[10px]" : "h-7 w-7 text-[11px]";
  return (
    <span
      aria-hidden="true"
      className={`inline-flex shrink-0 items-center justify-center rounded-full font-semibold ${dim}`}
      style={{ backgroundColor: tint.bg, color: tint.fg }}
    >
      {label}
    </span>
  );
}

// LONG / SHORT pill from the opening leg direction; em-dash when unknown.
export function DirectionBadge({
  direction,
}: {
  direction: string | null | undefined;
}): ReactNode {
  if (direction !== "long" && direction !== "short") {
    return <span className="text-on-surface-muted">—</span>;
  }
  const isLong = direction === "long";
  return (
    <span
      className={`inline-block rounded px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide ${
        isLong ? "bg-success-soft text-success" : "bg-primary/10 text-primary"
      }`}
    >
      {isLong ? "Long" : "Short"}
    </span>
  );
}

// Lifecycle status chip (Open / Closed / Partially closed). Neutral tone — the
// win/loss read comes from OutcomeBadge, not here.
export function StatusBadge({ status }: { status: string }): ReactNode {
  const label = status.replace(/_/g, " ");
  return (
    <span className="rounded bg-surface-container-low px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide text-on-surface-variant">
      {label}
    </span>
  );
}

// Win / Loss / Scratch chip derived from realized P&L sign. Renders nothing for
// an open trade (no realized outcome yet). Kept on primitive props so it stays
// free of API types like the rest of this module.
export function OutcomeBadge({
  status,
  realizedPnl,
}: {
  status: string;
  realizedPnl: string | null | undefined;
}): ReactNode {
  if (status.toLowerCase() === "open") return null;
  const pnl = Number(realizedPnl);
  if (!Number.isFinite(pnl) || pnl === 0) {
    return (
      <span className="rounded bg-surface-container-low px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide text-on-surface-variant">
        Scratch
      </span>
    );
  }
  const win = pnl > 0;
  return (
    <span
      className={`rounded px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide ${
        win ? "bg-success-soft text-success" : "bg-danger-soft text-danger"
      }`}
    >
      {win ? "Win" : "Loss"}
    </span>
  );
}

// Boolean "Note" chip (ADR 0036 D4): with one note per trade the count is only
// ever 0 or 1, so this is a presence affordance, not a count. It renders only
// when the trade has a note (notes_count > 0; a U4-era trade reporting N still
// reads as a single "Note"), and nothing otherwise so note-free rows stay
// uncluttered. It carries no click handler of its own -- it lives inside the
// trades-row click target, which opens the preview panel where the note is
// editable. Data-free like the other primitives.
export function TradeNoteBadge({ count }: { count: number }): ReactNode {
  if (count <= 0) return null;
  return (
    <span
      data-testid="trade-note-badge"
      title="Has a note"
      className="inline-flex items-center gap-0.5 rounded-full bg-surface-container-high px-1.5 py-0.5 text-[10px] font-semibold text-on-surface-variant"
    >
      <StickyNote aria-hidden="true" className="h-3 w-3" />
      Note
    </span>
  );
}

// The orange "N trades" pill on a scalper-run row header.
export function RunCountPill({ count }: { count: number }): ReactNode {
  return (
    <span className="rounded-full bg-primary/10 px-2 py-0.5 text-[11px] font-medium text-primary">
      {count} trades
    </span>
  );
}

// Largest absolute realized P&L across a set of rows — the scale a column of
// PnlGaugeBars shares so bar lengths are comparable within the rendered page.
export function maxAbsPnl(values: ReadonlyArray<string | null | undefined>): number {
  let max = 0;
  for (const value of values) {
    if (value === null || value === undefined) continue;
    const n = Math.abs(Number(value));
    if (Number.isFinite(n) && n > max) max = n;
  }
  return max;
}

// Diverging magnitude bar: fills right (green) for a profit, left (red) for a
// loss, scaled to `max` (the column's largest |P&L|). A flat track renders for
// a missing / zero value or an undefined scale.
export function PnlGaugeBar({
  value,
  max,
}: {
  value: string | null | undefined;
  max: number;
}): ReactNode {
  const n = value === null || value === undefined ? 0 : Number(value);
  const track = "relative h-1.5 w-full overflow-hidden rounded-full bg-surface-container-low";
  if (!Number.isFinite(n) || n === 0 || max <= 0) {
    return <div className={track} aria-hidden="true" />;
  }
  const halfPct = (Math.min(1, Math.abs(n) / max) * 50).toFixed(2);
  const positive = n > 0;
  return (
    <div className={track} aria-hidden="true">
      <div
        className={`absolute top-0 h-full ${positive ? "bg-success" : "bg-danger"}`}
        style={positive ? { left: "50%", width: `${halfPct}%` } : { right: "50%", width: `${halfPct}%` }}
      />
    </div>
  );
}

// Tag chips parsed from the trade's CSV tag labels; collapses the overflow into
// a "+N" pill. Renders an em-dash when the trade carries no tags.
export function TagPills({
  csv,
  limit = 3,
}: {
  csv: string | null | undefined;
  limit?: number;
}): ReactNode {
  const tags = (csv ?? "")
    .split(",")
    .map((tag) => tag.trim())
    .filter(Boolean);
  if (tags.length === 0) {
    return <span className="text-on-surface-muted">—</span>;
  }
  const shown = tags.slice(0, limit);
  const overflow = tags.length - shown.length;
  return (
    <span className="flex flex-wrap items-center gap-1">
      {shown.map((tag) => (
        <span
          key={tag}
          className="rounded-full border border-outline-variant bg-surface px-2 py-0.5 text-[11px] text-on-surface-variant"
        >
          {tag}
        </span>
      ))}
      {overflow > 0 ? (
        <span className="text-[11px] text-on-surface-muted">+{overflow}</span>
      ) : null}
    </span>
  );
}
